import { isDefined } from '@aloft/toolkit-shared';
import { Block, Input, theme } from '@aloft/toolkit-sui';

import { useRef, useState } from 'react';
import { Controller, ControllerRenderProps } from 'react-hook-form';

import { useAppDispatch } from '../../../redux/hooks.ts';
import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { usePropertyField } from './shared/usePropertyField.tsx';
import { usePropertyGridCellFocus } from './shared/usePropertyGridCellFocus.ts';
import {
  ALERT_TARGET_FIELD_TITLE,
  emptyFieldStyle,
  getAlertTargetInputFieldSx,
  popoverInputFocusSx,
} from './styles.ts';
import { PropertyGridFieldProps } from './types.ts';

export const SimpleEditableField = ({
  columnIndex,
  field,
  isArv = false,
  colSpan = 1,
  control,
  getValues,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();

  const { toEditValue, toValue } = field;

  const [currentParsedValue, setCurrentParsedValue] = useState<string | undefined>(undefined);

  const { propertyId, propertyDataFieldName, value, displayValue } = usePropertyField({
    columnIndex,
    field,
    isArv,
    control,
    getValues,
  });

  const fieldRef = useRef<HTMLInputElement>(null);
  const {
    isAlertHighlighted,
    isAlertStyled,
    alertStyleVariant,
    alertCellTooltip,
    clearAlertTarget,
  } = usePropertyGridCellFocus({
    cellKeyId: field.fieldName,
    propertyId,
    fieldRef,
    isArv,
    isFieldComplete:
      (displayValue !== undefined && displayValue !== '') ||
      (currentParsedValue !== undefined && currentParsedValue !== ''),
  });

  const handleSetFieldValue = (onChange: ControllerRenderProps['onChange'], newValue?: string) => {
    const parsedNewValue = toValue ? toValue(newValue, propertyId) : newValue;
    setCurrentParsedValue(parsedNewValue);
    onChange(parsedNewValue);

    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
    clearAlertTarget();
  };

  return (
    <Block gridColumn={`span ${colSpan}`} width="100%">
      <Controller
        name={propertyDataFieldName}
        control={control}
        render={({ field: { onChange } }) => {
          let editValue = toEditValue ? toEditValue(value, propertyId) : displayValue;
          const currentEditValue = toEditValue
            ? toEditValue(currentParsedValue, propertyId)
            : currentParsedValue;

          if (isDefined(currentParsedValue) && currentEditValue !== editValue) {
            editValue = currentEditValue as string;
          }

          const { title, backgroundColor } = emptyFieldStyle(displayValue === undefined);
          const alertTargetSx = getAlertTargetInputFieldSx(
            isAlertStyled,
            alertStyleVariant,
            isAlertHighlighted,
          );

          return (
            <Input
              ref={fieldRef}
              type="text"
              title={alertCellTooltip ?? (isAlertStyled ? ALERT_TARGET_FIELD_TITLE : title)}
              value={editValue}
              __dangerDoNotUseSx={{
                gridColumn: `span ${colSpan}`,
                backgroundColor: alertTargetSx?.backgroundColor ?? backgroundColor,
                border: 'none',
                boxShadow: alertTargetSx?.boxShadow,
                width: '100%',
                height: PROPERTY_GRID_ROW_HEIGHT,
                padding: '4px 24px 4px 8px',
                outline: 'none',
                borderRadius: '0',
                fontSize: '14px',
                _focusVisible: popoverInputFocusSx,
                _hover: alertTargetSx?._hover,
              }}
              onFocus={() => handleSetFieldValue(onChange, editValue)}
              onBlur={() => handleSetFieldValue(onChange, editValue)}
              onChange={(v) => handleSetFieldValue(onChange, v)}
              onClear={() => handleSetFieldValue(onChange, undefined)}
              onKeyDown={(e) => {
                const isEnterKey = e.key === 'Enter' || e.key === 'NumpadEnter';
                const isEscapeKey = e.key === 'Escape';

                if (isEnterKey) handleSetFieldValue(onChange, editValue);
                if (isEscapeKey || isEnterKey) {
                  (document.activeElement as HTMLElement | null)?.blur();
                }
              }}
            />
          );
        }}
      />
    </Block>
  );
};
